import { SESSION_TEXT_LAYER_ID } from '../MapBoxViewer/markupTextLayerIds'
import { isPointOverLayers } from './markupLayerHit'
import { MOVE_SLOP_PX } from './markupTextTap'
import { queryTextMarkups } from './TextMarkupGLLayer'

// Foreign markup labels open their info card; they always win a tap.
const REF_LABELS_LAYER_ID = 'markup-ref-labels'

export interface IMarkupTextCaptureHost {
  // Select tool, no pin edit, no gesture in progress.
  readonly canClaimText: boolean
  // Nothing selected on the Select tool: resting markups paint under the host's pins then.
  readonly isIdleSelect: boolean
  readonly isMobileEnv: boolean
  handleTextTap(id: string, x: number, y: number, t: number): void
  cancelTextTap(): void
  isEditingText(id: string): boolean
  setTextHover(isOver: boolean): void
}

/**
 * Taps on the editor's resting text, which is GL now and invisible to mapbox's and terra-draw's
 * own hit tests. Capture listeners on the canvas container claim a press on that text before
 * either sees it, and only presses aimed at the canvas: markers inside the container (pins, the
 * creation pin, the create bubble) keep theirs. The press is not prevented, so a drag still
 * pans and a pinch zooms; a claimed tap selects, a second one edits, and its click never
 * reaches the map's deselect.
 */
export class MarkupTextCapture {
  private claim: {
    pointerId: number
    id: string
    x: number
    y: number
    moved: boolean
  } | null = null
  private isClickLatched = false
  private isEditLatched = false
  private isHovering = false

  // The container is held, not re-read: after map.remove() the map can no longer give it.
  public constructor(
    private readonly map: mapboxgl.Map,
    private readonly container: HTMLElement,
    private readonly host: IMarkupTextCaptureHost,
    // The host's layers drawn above its resting text while it is idle.
    private readonly yields: {
      layerIds?: string[]
      textLayerIds?: string[]
    } = {},
  ) {
    container.addEventListener('pointerdown', this.onPointerDown, true)
    container.addEventListener('pointermove', this.onPointerMove, true)
    container.addEventListener('pointerup', this.onPointerUp, true)
    container.addEventListener('pointercancel', this.onPointerCancel, true)
    container.addEventListener('click', this.onClick, true)
    container.addEventListener('dblclick', this.onDblClick, true)
    container.addEventListener('touchend', this.onTouchEnd, true)
    container.addEventListener('mousemove', this.onMouseMove, true)
  }

  public dispose(): void {
    const c = this.container
    c.removeEventListener('pointerdown', this.onPointerDown, true)
    c.removeEventListener('pointermove', this.onPointerMove, true)
    c.removeEventListener('pointerup', this.onPointerUp, true)
    c.removeEventListener('pointercancel', this.onPointerCancel, true)
    c.removeEventListener('click', this.onClick, true)
    c.removeEventListener('dblclick', this.onDblClick, true)
    c.removeEventListener('touchend', this.onTouchEnd, true)
    c.removeEventListener('mousemove', this.onMouseMove, true)
    if (this.isHovering) this.host.setTextHover(false)
  }

  private point(e: MouseEvent): { x: number; y: number } {
    const rect = this.map.getCanvas().getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  // The resting text under the point, unless something drawn above it takes the tap: a foreign
  // label always, the host's yield layers while the editor is idle (paint order follows).
  private textAt(e: MouseEvent): string | null {
    if (e.target !== this.map.getCanvas() || !this.host.canClaimText) {
      return null
    }
    const point = this.point(e)
    const [id] = queryTextMarkups(this.map, point, [SESSION_TEXT_LAYER_ID])
    if (!id) return null
    const isIdle = this.host.isIdleSelect
    const isYielded = isPointOverLayers(
      this.map,
      point,
      [REF_LABELS_LAYER_ID, ...((isIdle && this.yields.layerIds) || [])],
      (isIdle && this.yields.textLayerIds) || [],
    )
    return isYielded ? null : id
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    this.isClickLatched = false
    this.isEditLatched = false
    if (this.claim) {
      // A second pointer is a pinch, not a tap. The claim stays, so the first pointer's up is
      // stopped with its down; terra-draw would take a lone up for a click.
      if (e.pointerId !== this.claim.pointerId) {
        this.claim.moved = true
        return
      }
      // The same pointer pressing again: its last up landed outside the map (a pan released
      // over the sidebar), so that claim is over.
      this.claim = null
    }
    if (!e.isPrimary || e.button !== 0) return
    const id = this.textAt(e)
    if (!id) return
    this.claim = {
      pointerId: e.pointerId,
      id,
      x: e.clientX,
      y: e.clientY,
      moved: false,
    }
    e.stopImmediatePropagation()
  }

  private readonly onPointerMove = (e: PointerEvent): void => {
    const c = this.claim
    if (
      c?.pointerId === e.pointerId &&
      Math.hypot(e.clientX - c.x, e.clientY - c.y) > MOVE_SLOP_PX
    ) {
      c.moved = true
    }
  }

  // Up is stopped with its down, so terra-draw never sees half a gesture.
  private readonly onPointerUp = (e: PointerEvent): void => {
    const c = this.claim
    if (c?.pointerId !== e.pointerId) return
    this.claim = null
    e.stopImmediatePropagation()
    if (c.moved) {
      this.host.cancelTextTap()
      return
    }
    this.isClickLatched = true
    this.host.handleTextTap(c.id, e.clientX, e.clientY, e.timeStamp)
    this.isEditLatched = this.host.isEditingText(c.id)
  }

  private readonly onPointerCancel = (e: PointerEvent): void => {
    if (this.claim?.pointerId === e.pointerId) this.claim = null
  }

  // The tap's own click would deselect it on the map.
  private readonly onClick = (e: MouseEvent): void => {
    if (!this.isClickLatched) return
    this.isClickLatched = false
    e.stopImmediatePropagation()
  }

  // An edit made by a double-tap must not also zoom the map.
  private readonly onDblClick = (e: MouseEvent): void => {
    if (!this.isEditLatched) return
    this.isEditLatched = false
    e.stopImmediatePropagation()
  }

  private readonly onTouchEnd = (e: TouchEvent): void => {
    if (this.isEditLatched) e.stopImmediatePropagation()
  }

  // Desktop only, and never mid-drag: a resize drag has no pointer capture to protect it.
  private readonly onMouseMove = (e: MouseEvent): void => {
    if (this.host.isMobileEnv || e.buttons !== 0) return
    const isOver = !!this.textAt(e)
    if (isOver) e.stopImmediatePropagation()
    if (isOver !== this.isHovering) {
      this.isHovering = isOver
      this.host.setTextHover(isOver)
    }
  }
}
